import { RRule } from 'rrule';
import dayjs from 'dayjs';

/**
 * Tiện ích cho ô "Kiểu lặp" (loop_info) của sự kiện hệ thống.
 *
 * loop_info trong DB là chuỗi RRULE trần, KHÔNG có tiền tố "RRULE:" và không có
 * dòng DTSTART (VD: "FREQ=YEARLY;INTERVAL=1"). rule.toString() của rrule.js lại
 * thêm các phần đó, nên chuỗi luôn được tự ghép trong buildRRuleString thay vì
 * lấy thẳng từ thư viện — tránh ghi vào DB một định dạng khác CMS cũ.
 */

export const FREQ_OPTIONS = [
  { label: 'Hằng năm', value: 'YEARLY' },
  { label: 'Hằng tháng', value: 'MONTHLY' },
  { label: 'Hằng tuần', value: 'WEEKLY' },
  { label: 'Hằng ngày', value: 'DAILY' },
];

const FREQ_TO_CONST = {
  YEARLY: RRule.YEARLY,
  MONTHLY: RRule.MONTHLY,
  WEEKLY: RRule.WEEKLY,
  DAILY: RRule.DAILY,
};

const FREQ_UNIT = { YEARLY: 'năm', MONTHLY: 'tháng', WEEKLY: 'tuần', DAILY: 'ngày' };

// Thứ tự theo quy ước RRULE: MO = 0
export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const WEEKDAY_OPTIONS = [
  { label: 'T2', value: 'MO' },
  { label: 'T3', value: 'TU' },
  { label: 'T4', value: 'WE' },
  { label: 'T5', value: 'TH' },
  { label: 'T6', value: 'FR' },
  { label: 'T7', value: 'SA' },
  { label: 'CN', value: 'SU' },
];

const WEEKDAY_LABEL = WEEKDAY_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ label: `Tháng ${i + 1}`, value: i + 1 }));

export const MONTHDAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ label: `Ngày ${i + 1}`, value: i + 1 }));

export const SETPOS_OPTIONS = [
  { label: 'Lần 1', value: 1 },
  { label: 'Lần 2', value: 2 },
  { label: 'Lần 3', value: 3 },
  { label: 'Lần 4', value: 4 },
  { label: 'Kế cuối', value: -2 },
  { label: 'Cuối cùng', value: -1 },
];

// Dùng cho câu mô tả ("vào T6 cuối cùng của tháng"), khác nhãn ngắn trong ô chọn
const SETPOS_PHRASE = { 1: 'đầu tiên', 2: 'thứ hai', 3: 'thứ ba', 4: 'thứ tư', '-2': 'kế cuối', '-1': 'cuối cùng' };

export const DEFAULT_STATE = {
  freq: 'YEARLY',
  interval: 1,
  byweekday: [],
  bymonth: [],
  bymonthday: [],
  monthlyMode: 'day',
  bysetpos: 1,
  endMode: 'never',
  count: 30,
  until: null,
};

const toArray = (value) => {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
};

/**
 * byweekday sau parseString là mảng Weekday (có .weekday, và .n nếu chuỗi ghi kiểu
 * "BYDAY=-2FR") hoặc số. Quy về { codes, nth }: nth phải được giữ lại, nếu bỏ thì
 * "thứ Sáu kế cuối" mở ra modal rồi bấm OK sẽ âm thầm thành "thứ Sáu đầu tháng".
 */
const readWeekdays = (raw) => {
  const items = toArray(raw).map((d) => (typeof d === 'number' ? { weekday: d } : d || {}));
  const valid = items.filter((d) => Number.isInteger(d.weekday) && d.weekday >= 0 && d.weekday < 7);
  const nth = valid.map((d) => d.n).find((n) => Number.isInteger(n));
  return { codes: valid.map((d) => WEEKDAY_CODES[d.weekday]), nth: nth ?? null };
};

const formatUntil = (value) => {
  const d = dayjs(value);
  if (!d.isValid()) return null;
  // UNTIL theo RFC 5545; lấy cuối ngày để chính ngày được chọn vẫn được tính là một lần lặp
  return d.endOf('day').format('YYYYMMDD[T]HHmmss[Z]');
};

/**
 * options.until là Date hiểu theo UTC. Ở GMT+7, dayjs(date) rồi endOf('day') sẽ đẩy
 * "20/01 23:59:59Z" thành 21/01 — mỗi lần mở modal rồi lưu lại là UNTIL trôi thêm
 * một ngày. Đọc lại đúng ngày theo lịch UTC để round-trip đứng yên.
 */
const untilToDayjs = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return dayjs(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

/** Ghép state của form thành chuỗi RRULE trần để lưu vào loop_info. */
export const buildRRuleString = (state) => {
  const s = { ...DEFAULT_STATE, ...(state || {}) };
  const parts = [`FREQ=${s.freq}`];

  const interval = Number(s.interval) || 1;
  parts.push(`INTERVAL=${interval}`);

  if (s.freq === 'WEEKLY' && s.byweekday.length) {
    parts.push(`BYDAY=${s.byweekday.join(',')}`);
  }

  if (s.freq === 'MONTHLY') {
    if (s.monthlyMode === 'weekday') {
      if (s.byweekday.length) parts.push(`BYDAY=${s.byweekday.join(',')}`);
      if (s.bysetpos) parts.push(`BYSETPOS=${s.bysetpos}`);
    } else if (s.bymonthday.length) {
      parts.push(`BYMONTHDAY=${s.bymonthday.join(',')}`);
    }
  }

  if (s.freq === 'YEARLY') {
    if (s.bymonth.length) parts.push(`BYMONTH=${s.bymonth.join(',')}`);
    if (s.bymonthday.length) parts.push(`BYMONTHDAY=${s.bymonthday.join(',')}`);
  }

  if (s.endMode === 'count' && Number(s.count) > 0) parts.push(`COUNT=${Number(s.count)}`);
  if (s.endMode === 'until' && s.until) {
    const until = formatUntil(s.until);
    if (until) parts.push(`UNTIL=${until}`);
  }

  return parts.join(';');
};

/** Đọc ngược chuỗi loop_info sẵn có để mở lại đúng lựa chọn trong modal. */
export const parseRRuleString = (raw) => {
  const text = String(raw || '')
    .replace(/^RRULE:/i, '')
    .trim();
  if (!text || /^FREQ=NONE$/i.test(text)) return { ...DEFAULT_STATE };

  let options;
  try {
    options = RRule.parseString(text);
  } catch (e) {
    return { ...DEFAULT_STATE };
  }

  const freq = Object.keys(FREQ_TO_CONST).find((k) => FREQ_TO_CONST[k] === options.freq);
  if (!freq) return { ...DEFAULT_STATE };

  const { codes: byweekday, nth } = readWeekdays(options.byweekday);
  const bymonthday = toArray(options.bymonthday).map(Number);
  const bysetpos = toArray(options.bysetpos).map(Number)[0] ?? nth;

  return {
    ...DEFAULT_STATE,
    freq,
    interval: options.interval || 1,
    byweekday,
    bymonth: toArray(options.bymonth).map(Number),
    bymonthday,
    // MONTHLY chọn theo thứ khi có BYDAY (dù BYSETPOS trống, VD "BYDAY=-1FR")
    monthlyMode: freq === 'MONTHLY' && byweekday.length ? 'weekday' : 'day',
    bysetpos: bysetpos || DEFAULT_STATE.bysetpos,
    endMode: options.count ? 'count' : options.until ? 'until' : 'never',
    count: options.count || DEFAULT_STATE.count,
    until: options.until ? untilToDayjs(options.until) : null,
  };
};

/**
 * dtstart của rrule.js được diễn giải theo UTC. Đẩy các thành phần ngày/giờ local
 * vào Date.UTC (và đọc lại bằng getUTC*) để ngày xem trước khớp đúng ngày người
 * dùng nhập, không bị lệch theo múi giờ máy.
 */
const toUtcDate = (value) => {
  const d = dayjs(value);
  const base = d.isValid() ? d : dayjs();
  return new Date(Date.UTC(base.year(), base.month(), base.date(), base.hour(), base.minute(), base.second()));
};

const formatUtcDate = (date) =>
  `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;

/**
 * Danh sách vài lần lặp đầu tiên để người dùng đối chiếu trước khi lưu.
 * Trả { dates, error } — chuỗi sai cú pháp không được ném ra ngoài modal.
 */
export const previewOccurrences = (rruleString, dtstart, limit = 8) => {
  const text = String(rruleString || '').trim();
  if (!text) return { dates: [], error: null };
  try {
    const options = RRule.parseString(text);
    const rule = new RRule({ ...options, dtstart: toUtcDate(dtstart) });
    const dates = rule.all((_, i) => i < limit).map(formatUtcDate);
    return { dates, error: null };
  } catch (e) {
    return { dates: [], error: e?.message || 'Chuỗi lặp không hợp lệ' };
  }
};

/** Mô tả chuỗi RRULE bằng tiếng Việt để hiển thị dưới ô nhập (giống loopInfo của CMS cũ). */
export const describeRRule = (raw) => {
  const text = String(raw || '')
    .replace(/^RRULE:/i, '')
    .trim();
  if (!text || /^FREQ=NONE$/i.test(text)) return '';

  let options;
  try {
    options = RRule.parseString(text);
  } catch (e) {
    return 'Chuỗi lặp không hợp lệ';
  }

  const freq = Object.keys(FREQ_TO_CONST).find((k) => FREQ_TO_CONST[k] === options.freq);
  if (!freq) return 'Chuỗi lặp không hợp lệ';

  const interval = options.interval || 1;
  const parts = [interval > 1 ? `Mỗi ${interval} ${FREQ_UNIT[freq]}` : `Mỗi ${FREQ_UNIT[freq]}`];

  const { codes: weekdays, nth } = readWeekdays(options.byweekday);
  const monthdays = toArray(options.bymonthday).map(Number);
  const months = toArray(options.bymonth).map(Number);
  const setpos = toArray(options.bysetpos).map(Number)[0] ?? nth;

  if (weekdays.length) {
    const days = weekdays.map((c) => WEEKDAY_LABEL[c] || c).join(', ');
    parts.push(setpos ? `vào ${days} ${SETPOS_PHRASE[setpos] || `lần ${setpos}`} của tháng` : `vào ${days}`);
  }
  if (monthdays.length) parts.push(`vào ngày ${monthdays.join(', ')}`);
  if (months.length) parts.push(`của tháng ${months.join(', ')}`);

  if (options.count) parts.push(`, lặp ${options.count} lần`);
  if (options.until) parts.push(`, đến ${untilToDayjs(options.until)?.format('DD/MM/YYYY') || ''}`);

  return parts.join(' ').replace(/\s+,/g, ',');
};
